import type { StatusGet200DataPublicFlags } from '@/api/schemas';
import type { NavItem } from '@/components/sidebar/NavGroup';
import { useHubQueueCount } from '@/hooks/useHubQueueCount';
import { hasAnyPermissionOrFalse, usePrincipal } from '@/hooks/usePrincipal';
import { usePublicFlag } from '@/hooks/usePublicFlag';
import { hubQueueBadgeLabel } from '@/pages/admin/configs/hub-queue';
import { ADMIN_PERMISSIONS } from '@/routes/admin.routes';
import {
  BarChart3,
  BookOpen,
  ClipboardList,
  Home,
  LayoutDashboard,
  User,
  Users,
  Wand2,
} from 'lucide-react';

/**
 * The sidebar's IA. A `section` groups items under an optional header;
 * each item carries its router target, label, icon, and optional
 * `requiredPermissions` (OR semantics).
 *
 * Permission gating today: per-item `requiredPermissions` is the source of
 * truth READ BY THE SIDEBAR. The Admin entry mirrors the union permission
 * list on the `/admin/*` route handle (`ADMIN_PERMISSIONS` in
 * `admin.routes.tsx`). G4-T18 lifts permissions onto per-tab nested route
 * handles, at which point this file will pull the values off the route
 * handles directly (no more import).
 *
 * This file's own `NavItem.requiredPermissions` (not the route handle's) is
 * what the sidebar checks — and it's omitted for every item in
 * HOME_SECTION/CATALOG_SECTION/PERSONAL_SECTION below, so `useNavSections`
 * shows them to every authenticated user (see the `undefined` check in
 * `useNavSections`). That is NOT the same as the underlying route handles:
 * `/` (dashboard), `/submissions`, and `/me` do declare a true
 * `requiredPermissions: []` on their handles, but `/catalog` and `/teams`
 * declare `['plugins:view']` (a real permission check, granted to every
 * role per Appendix A — universal today, but enforced by `RequireAuth`,
 * not skipped). Detail routes (`/catalog/:slug`, `/teams/:slug`) and
 * `/admin/*` declare their own non-empty permission sets on their handles.
 */
/**
 * `publicFlagKey` gates an item behind an anonymous-safe `/api/status`
 * `publicFlags` entry, fail-closed. Its key type is DERIVED from the
 * generated `StatusGet200DataPublicFlags` — the same type `usePublicFlag`
 * accepts — so adding a flag to `publicFlags` in `endpoints/status.ts` and
 * re-running `pnpm orval` extends this union automatically; a hand-written
 * copy would drift from the contract by construction.
 *
 * The field generalises what used to be a hard-coded
 * `item.to === '/submissions'` special case in `useNavSections`: the second
 * flag-gated nav item (`/metrics`) made the special case a pattern, and a
 * third would have made it a bug waiting to happen.
 *
 * The flag hides the NAV ENTRY only — the route stays registered, so a direct
 * URL lands on the page's own `ComingSoon` gate rather than a 404.
 */
export interface NavSection {
  label?: string;
  items: ReadonlyArray<
    NavItem & {
      requiredPermissions?: readonly string[];
      publicFlagKey?: keyof StatusGet200DataPublicFlags;
    }
  >;
}

const HOME_SECTION: NavSection = {
  items: [{ to: '/dashboard', label: 'Dashboard', Icon: Home }],
};

const CATALOG_SECTION: NavSection = {
  label: 'Catalog',
  items: [
    { to: '/catalog', label: 'Plugins', Icon: Wand2 },
    { to: '/teams', label: 'Teams', Icon: Users },
  ],
};

const PERSONAL_SECTION: NavSection = {
  label: 'Personal',
  items: [
    {
      to: '/submissions',
      label: 'My submissions',
      Icon: ClipboardList,
      publicFlagKey: 'my-submissions-enabled',
    },
    { to: '/me', label: 'Profile', Icon: User },
  ],
};

/**
 * Metrics sits in its own section rather than under Admin: `metrics:view` is
 * deliberately absent from `ADMIN_PERMISSIONS`, so a `metrics_viewer` sees
 * this entry and nothing else administrative.
 */
const INSIGHTS_SECTION: NavSection = {
  label: 'Insights',
  items: [
    {
      to: '/metrics',
      label: 'Metrics',
      Icon: BarChart3,
      requiredPermissions: ['metrics:view'],
      publicFlagKey: 'metrics-dashboard',
    },
  ],
};

// Dev hits the Astro dev server directly on its own origin — proxying
// through Vite breaks because Astro's HMR HTML references absolute paths
// (`/@vite/client`, `/@id/*`, `/node_modules/*`) that collide with the
// UI's own Vite. Prod stays at `/docs/`, served by SWA from the static
// Starlight build. `external: true` keeps both cases out of the router.
const DOCS_URL = import.meta.env.DEV ? 'http://localhost:4321/docs/' : '/docs/';

const DOCS_SECTION: NavSection = {
  label: 'Docs',
  items: [{ to: DOCS_URL, label: 'Docs', Icon: BookOpen, external: true }],
};

/**
 * Admin section collapsed to a single Dashboard entry — the page itself
 * owns per-tab routing via hash, so the sidebar no longer mirrors every
 * tab. `match: 'prefix'` so the entry stays active across `/admin` and
 * any future nested admin route. Gating uses the union permission list
 * imported from the route handle.
 */
const ADMIN_SECTION: NavSection = {
  label: 'Admin',
  items: [
    {
      to: '/admin',
      label: 'Dashboard',
      Icon: LayoutDashboard,
      match: 'prefix',
      requiredPermissions: ADMIN_PERMISSIONS,
    },
  ],
};

export const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  HOME_SECTION,
  CATALOG_SECTION,
  PERSONAL_SECTION,
  INSIGHTS_SECTION,
  DOCS_SECTION,
  ADMIN_SECTION,
];

/**
 * Returns NAV_SECTIONS with permission-gated items filtered against the
 * current principal. Sections that go empty after filtering are dropped
 * so the sidebar doesn't render an orphan label.
 *
 * Items with no `requiredPermissions` (top-level routes) always render
 * once authenticated — SWA's `/*` rule keeps anonymous viewers out of
 * the SPA entirely, so the principal hook will have resolved before
 * `AppShell` mounts in prod.
 *
 * Items carrying a `publicFlagKey` are additionally gated behind the
 * anonymous-safe `/api/status` `publicFlags` entry (`usePublicFlag`,
 * fail-closed): while the feature is off the nav entry is hidden, but the
 * route itself stays registered — a direct URL lands on the page's own
 * ComingSoon gate.
 */
export function useNavSections(): NavSection[] {
  const principal = usePrincipal();
  const hubQueueCount = useHubQueueCount();
  // Both public flags are read unconditionally — hooks cannot be called per
  // item, and `/api/status` is a single shared query either way.
  const { enabled: mySubmissionsEnabled } = usePublicFlag('my-submissions-enabled');
  const { enabled: metricsEnabled } = usePublicFlag('metrics-dashboard');
  // A TOTAL record over the contract's flag keys, not `Record<string, …>`:
  // adding a flag to `publicFlags` and gating a nav item on it now fails to
  // compile until the `usePublicFlag` read is wired here too, instead of
  // silently resolving `undefined` and hiding the item forever.
  const publicFlags: Record<keyof StatusGet200DataPublicFlags, boolean> = {
    'my-submissions-enabled': mySubmissionsEnabled,
    'metrics-dashboard': metricsEnabled,
  };
  return NAV_SECTIONS.flatMap((section) => {
    const visibleItems = section.items
      .filter((item) => {
        if (item.publicFlagKey !== undefined && !publicFlags[item.publicFlagKey]) return false;
        if (item.requiredPermissions === undefined) return true;
        return hasAnyPermissionOrFalse(principal, item.requiredPermissions);
      })
      .map((item) =>
        item.to === '/admin' && hubQueueCount > 0
          ? {
              ...item,
              badge: { count: hubQueueCount, ariaLabel: hubQueueBadgeLabel(hubQueueCount) },
            }
          : item,
      );
    if (visibleItems.length === 0) return [];
    return [{ ...section, items: visibleItems }];
  });
}
